/**
 * DiscoveryView Component
 *
 * Professional discovery analysis visualization with:
 * - Executive Summary with ROI analysis
 * - Business Processes discovery
 * - Integration Points analysis
 * - Migration Roadmap visualization
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import * as d3 from 'd3'
import {
  ArrowLeft, Compass, TrendingUp, Workflow, Link2, Map,
  Loader2, AlertCircle, DollarSign, Calendar,
  CheckCircle, Clock, Target, Zap, ArrowUpRight,
  Server, Award, BarChart2, Layers, Shield,
  Cloud, ChevronRight, Activity, Code2
} from 'lucide-react'
import { getDiscoveryResults } from '../../services/codeAnalysisService'
import './DiscoveryView.css'

/**
 * Tab configuration
 */
const TABS = {
  'summary': {
    label: 'Executive Summary',
    icon: TrendingUp,
    description: 'ROI and key metrics',
  },
  'processes': {
    label: 'Business Processes',
    icon: Workflow,
    description: 'Discovered business logic',
  },
  'integrations': {
    label: 'Integration Points',
    icon: Link2,
    description: 'System integrations',
  },
  'roadmap': {
    label: 'Migration Roadmap',
    icon: Map,
    description: 'Phased migration plan',
  },
}

/**
 * Format a number as currency: $82,865 / $1.2M
 */
function formatCurrency(val) {
  if (val == null) return 'N/A'
  const n = Number(val)
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`
  return `$${n.toLocaleString()}`
}

/**
 * Format a large ROI percentage readably
 */
function formatRoiPct(val) {
  if (val == null || val === 0) return '0%'
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M%`
  if (val >= 1_000) return `${Math.round(val).toLocaleString()}%`
  return `${Math.round(val)}%`
}

/**
 * Classify ROI into a tier
 */
function getRoiClassification(roi) {
  if (roi >= 1000) return { label: 'EXCEPTIONAL', color: '#9ece6a', bg: 'rgba(158, 206, 106, 0.12)', border: 'rgba(158, 206, 106, 0.3)' }
  if (roi >= 500) return { label: 'EXCELLENT', color: '#7aa2f7', bg: 'rgba(122, 162, 247, 0.12)', border: 'rgba(122, 162, 247, 0.3)' }
  if (roi >= 200) return { label: 'STRONG', color: '#e0af68', bg: 'rgba(224, 175, 104, 0.12)', border: 'rgba(224, 175, 104, 0.3)' }
  if (roi >= 100) return { label: 'GOOD', color: '#7dcfff', bg: 'rgba(125, 207, 255, 0.12)', border: 'rgba(125, 207, 255, 0.3)' }
  return { label: 'MARGINAL', color: '#f7768e', bg: 'rgba(247, 118, 142, 0.12)', border: 'rgba(247, 118, 142, 0.3)' }
}

/**
 * Get phase duration weeks from multiple possible field names
 */
function getPhaseWeeks(phase) {
  return phase.estimated_effort_weeks
    ?? phase.duration_weeks
    ?? phase.effort_weeks
    ?? phase.duration
    ?? phase.weeks
    ?? null
}

/**
 * AWS service color map for recognizable brand-adjacent palette
 */
const AWS_SERVICE_COLORS = {
  'AWS Lambda': { bg: 'rgba(255, 153, 0, 0.15)', color: '#ff9900', border: 'rgba(255, 153, 0, 0.35)' },
  'Lambda': { bg: 'rgba(255, 153, 0, 0.15)', color: '#ff9900', border: 'rgba(255, 153, 0, 0.35)' },
  'AWS Batch': { bg: 'rgba(255, 153, 0, 0.15)', color: '#ff9900', border: 'rgba(255, 153, 0, 0.35)' },
  'Step Functions': { bg: 'rgba(122, 162, 247, 0.15)', color: '#7aa2f7', border: 'rgba(122, 162, 247, 0.35)' },
  'AWS Step Functions': { bg: 'rgba(122, 162, 247, 0.15)', color: '#7aa2f7', border: 'rgba(122, 162, 247, 0.35)' },
  'S3': { bg: 'rgba(158, 206, 106, 0.15)', color: '#9ece6a', border: 'rgba(158, 206, 106, 0.35)' },
  'Amazon S3': { bg: 'rgba(158, 206, 106, 0.15)', color: '#9ece6a', border: 'rgba(158, 206, 106, 0.35)' },
  'RDS': { bg: 'rgba(125, 207, 255, 0.15)', color: '#7dcfff', border: 'rgba(125, 207, 255, 0.35)' },
  'Amazon RDS': { bg: 'rgba(125, 207, 255, 0.15)', color: '#7dcfff', border: 'rgba(125, 207, 255, 0.35)' },
  'ECS': { bg: 'rgba(187, 154, 247, 0.15)', color: '#bb9af7', border: 'rgba(187, 154, 247, 0.35)' },
  'Fargate': { bg: 'rgba(187, 154, 247, 0.15)', color: '#bb9af7', border: 'rgba(187, 154, 247, 0.35)' },
  'SQS': { bg: 'rgba(224, 175, 104, 0.15)', color: '#e0af68', border: 'rgba(224, 175, 104, 0.35)' },
}

function getAwsBadgeStyle(service) {
  return AWS_SERVICE_COLORS[service] || { bg: 'rgba(122, 162, 247, 0.12)', color: '#7aa2f7', border: 'rgba(122, 162, 247, 0.3)' }
}

/**
 * AWS service badge component
 */
function AWSServiceBadge({ service }) {
  const style = getAwsBadgeStyle(service)
  return (
    <span
      className="aws-service-badge"
      style={{ background: style.bg, color: style.color, borderColor: style.border }}
    >
      <Cloud size={10} />
      {service}
    </span>
  )
}

/**
 * Executive Summary Tab — redesigned for business impact
 */
function ExecutiveSummaryTab({ data }) {
  if (!data?.roi_analysis && !data?.executive_summary) {
    return (
      <div className="empty-state">
        <p>No executive summary available</p>
        <span>Run discovery analysis to generate ROI data</span>
      </div>
    )
  }

  const roi = data.roi_analysis?.headline_metrics || {}
  const costBreakdown = data.roi_analysis?.cost_breakdown || {}
  const exec = data.executive_summary || {}
  const businessProcesses = data.business_processes?.business_processes || []
  const integrations = data.integration_points?.integration_points || []
  const highValueProcesses = businessProcesses.filter(p => p.business_value === 'High')
  const roadmapData = data.migration_roadmap || {}
  const roadmapPhases = roadmapData.phases || []
  const archRecs = data.architecture_recommendations || {}

  const roiValue = roi.five_year_roi_percent || 0
  const roiClass = getRoiClassification(roiValue)

  // Parse cost breakdown — try multiple field name patterns from different AI engines
  const legacyCost = costBreakdown.legacy_annual_cost
    || costBreakdown.mainframe_annual_cost
    || costBreakdown.current_annual_cost
    || 50000
  const awsCost = costBreakdown.aws_annual_cost
    || costBreakdown.cloud_annual_cost
    || costBreakdown.target_annual_cost
    || 7500
  const infraSavingsPct = costBreakdown.infrastructure_savings_percent
    || costBreakdown.infra_savings_percent
    || (legacyCost > 0 ? Math.round((1 - awsCost / legacyCost) * 100) : 85)

  const manualDevCost = costBreakdown.manual_rewrite_cost
    || costBreakdown.manual_dev_cost
    || 702
  const aiDevCost = costBreakdown.ai_transformation_cost
    || costBreakdown.ai_dev_cost
    || roi.total_investment
    || 66
  const devSavingsPct = costBreakdown.dev_time_savings_percent
    || costBreakdown.development_savings_percent
    || (manualDevCost > 0 ? Math.round((1 - aiDevCost / manualDevCost) * 100) : 91)

  const hasArchRec = archRecs.primary_pattern
    || archRecs.architecture_type
    || archRecs.recommended_services?.length > 0

  return (
    <div className="executive-summary">

      {/* ── ROI Hero Banner ── */}
      <div className="roi-hero" style={{ borderColor: roiClass.border }}>
        <div className="roi-hero-left">
          <div
            className="roi-classification-badge"
            style={{ background: roiClass.bg, borderColor: roiClass.border }}
          >
            <Award size={14} style={{ color: roiClass.color }} />
            <span style={{ color: roiClass.color }}>{roiClass.label} ROI</span>
          </div>
          <div className="roi-big-number" style={{ color: roiClass.color }}>
            {formatRoiPct(roiValue)}
          </div>
          <div className="roi-subtitle">5-Year Return on Investment</div>
          <div className="roi-arrow-story">
            <div className="arrow-story-item investment">
              <span className="arrow-story-label">Investment</span>
              <span className="arrow-story-amount">{formatCurrency(roi.total_investment)}</span>
            </div>
            <ChevronRight size={20} className="arrow-story-chevron" />
            <div className="arrow-story-item value">
              <span className="arrow-story-label">5-Year Value</span>
              <span className="arrow-story-amount" style={{ color: '#9ece6a' }}>
                {formatCurrency(roi.five_year_savings)}
              </span>
            </div>
          </div>
        </div>
        <div className="roi-hero-right">
          <div className="roi-stat-grid">
            <div className="roi-stat">
              <Clock size={18} className="roi-stat-icon" />
              <div>
                <div className="roi-stat-value">
                  {roi.payback_months > 0 ? `${roi.payback_months} mo` : '< 1 month'}
                </div>
                <div className="roi-stat-label">Payback Period</div>
              </div>
            </div>
            <div className="roi-stat">
              <TrendingUp size={18} className="roi-stat-icon success" />
              <div>
                <div className="roi-stat-value">{formatCurrency(roi.annual_recurring_savings)}</div>
                <div className="roi-stat-label">Annual Savings</div>
              </div>
            </div>
            <div className="roi-stat">
              <BarChart2 size={18} className="roi-stat-icon purple" />
              <div>
                <div className="roi-stat-value">
                  {formatCurrency(roi.net_present_value || roi.npv)}
                </div>
                <div className="roi-stat-label">Net Present Value</div>
              </div>
            </div>
            <div className="roi-stat">
              <Target size={18} className="roi-stat-icon warning" />
              <div>
                <div className="roi-stat-value">{businessProcesses.length}</div>
                <div className="roi-stat-label">Processes Found</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cost Comparison ── */}
      <div className="cost-comparison-section">
        <h4><DollarSign size={16} /> Cost Analysis</h4>
        <div className="cost-comparison-grid">
          <div className="cost-comparison-card">
            <div className="cost-comp-title">Infrastructure Costs (Annual)</div>
            <div className="cost-bars">
              <div className="cost-bar-row">
                <span className="cost-bar-label">Legacy Mainframe</span>
                <div className="cost-bar-track">
                  <div className="cost-bar-fill legacy" style={{ width: '100%' }}>
                    <span className="cost-bar-value">{formatCurrency(legacyCost)}/yr</span>
                  </div>
                </div>
              </div>
              <div className="cost-bar-row">
                <span className="cost-bar-label">AWS Cloud</span>
                <div className="cost-bar-track">
                  <div
                    className="cost-bar-fill aws"
                    style={{ width: `${Math.max(5, 100 - infraSavingsPct)}%` }}
                  >
                    <span className="cost-bar-value">{formatCurrency(awsCost)}/yr</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="cost-saving-badge">
              <Shield size={12} />
              {infraSavingsPct}% infrastructure cost reduction
            </div>
          </div>

          <div className="cost-comparison-card">
            <div className="cost-comp-title">Development Cost</div>
            <div className="cost-bars">
              <div className="cost-bar-row">
                <span className="cost-bar-label">Manual Rewrite</span>
                <div className="cost-bar-track">
                  <div className="cost-bar-fill legacy" style={{ width: '100%' }}>
                    <span className="cost-bar-value">{formatCurrency(manualDevCost)}</span>
                  </div>
                </div>
              </div>
              <div className="cost-bar-row">
                <span className="cost-bar-label">AI Transformation</span>
                <div className="cost-bar-track">
                  <div
                    className="cost-bar-fill aws"
                    style={{ width: `${Math.max(5, 100 - devSavingsPct)}%` }}
                  >
                    <span className="cost-bar-value">{formatCurrency(aiDevCost)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="cost-saving-badge">
              <Zap size={12} />
              {devSavingsPct}% development cost reduction
            </div>
          </div>
        </div>
      </div>

      {/* ── Architecture Recommendation ── */}
      {hasArchRec && (
        <div className="arch-rec-section">
          <h4><Server size={16} /> Architecture Recommendation</h4>
          <div className="arch-rec-card">
            <div className="arch-rec-inner">
              <Layers size={22} className="arch-rec-icon" />
              <div className="arch-rec-body">
                <div className="arch-rec-name">
                  {archRecs.primary_pattern || archRecs.architecture_type || 'Cloud-Native Microservices'}
                </div>
                {archRecs.description && (
                  <div className="arch-rec-desc">{archRecs.description}</div>
                )}
                {archRecs.recommended_services?.length > 0 && (
                  <div className="arch-services">
                    {archRecs.recommended_services.map((svc, i) => (
                      <AWSServiceBadge key={i} service={svc?.name || svc} />
                    ))}
                  </div>
                )}
              </div>
              <div className="arch-rec-meta">
                {archRecs.confidence_score != null && (
                  <div className="arch-confidence">
                    <div className="confidence-ring">
                      <span className="confidence-value">
                        {Math.round(archRecs.confidence_score * 100)}%
                      </span>
                    </div>
                    <div className="confidence-label">Confidence</div>
                  </div>
                )}
                {archRecs.estimated_monthly_cost != null && (
                  <div className="arch-cost-pill">
                    {formatCurrency(archRecs.estimated_monthly_cost)}/mo
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Scope & Timeline ── */}
      <div className="summary-grid">
        <div className="summary-section">
          <h4><Target size={16} /> Project Scope</h4>
          <div className="stat-list">
            <div className="stat-item">
              <span className="stat-label">Business Processes</span>
              <span className="stat-value">{businessProcesses.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">High-Value Processes</span>
              <span className="stat-value highlight">{highValueProcesses.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Integration Points</span>
              <span className="stat-value">{integrations.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Migration Phases</span>
              <span className="stat-value">{roadmapPhases.length}</span>
            </div>
          </div>
        </div>

        <div className="summary-section">
          <h4><Calendar size={16} /> Timeline</h4>
          <div className="stat-list">
            <div className="stat-item">
              <span className="stat-label">Total Duration</span>
              <span className="stat-value">{roadmapData.timeline?.total_months || 0} months</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Weeks</span>
              <span className="stat-value">{roadmapData.timeline?.total_weeks || 0} weeks</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Key Recommendations ── */}
      {exec.key_recommendations?.length > 0 && (
        <div className="recommendations-section">
          <h4><Zap size={16} /> Key Recommendations</h4>
          <ul className="recommendations-list">
            {exec.key_recommendations.map((rec, i) => (
              <li key={i}>
                <CheckCircle size={14} />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Next Steps ── */}
      {exec.next_steps?.length > 0 && (
        <div className="next-steps-section">
          <h4><ArrowUpRight size={16} /> Next Steps</h4>
          <ol className="next-steps-list">
            {exec.next_steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

/**
 * Business Processes Tab — enhanced with cloud readiness + AWS recs
 */
function BusinessProcessesTab({ data }) {
  const [selectedProcess, setSelectedProcess] = useState(null)

  const processes = data?.business_processes?.business_processes || []

  if (processes.length === 0) {
    return (
      <div className="empty-state">
        <p>No business processes found</p>
        <span>Discovery analysis will identify business logic</span>
      </div>
    )
  }

  const grouped = useMemo(() => {
    const groups = { High: [], Medium: [], Low: [] }
    processes.forEach(p => {
      const value = p.business_value || 'Medium'
      if (groups[value]) groups[value].push(p)
      else groups['Medium'].push(p)
    })
    return groups
  }, [processes])

  const readinessColor = (score) => {
    if (score >= 70) return '#9ece6a'
    if (score >= 40) return '#e0af68'
    return '#f7768e'
  }

  return (
    <div className="business-processes">
      <div className="process-list">
        {Object.entries(grouped).map(([value, procs]) => (
          procs.length > 0 && (
            <div key={value} className="process-group">
              <h4 className={`group-header ${value.toLowerCase()}`}>
                {value} Value ({procs.length})
              </h4>
              {procs.map((proc, i) => (
                <div
                  key={i}
                  className={`process-item ${selectedProcess === proc ? 'selected' : ''}`}
                  onClick={() => setSelectedProcess(selectedProcess === proc ? null : proc)}
                >
                  <div className="process-header">
                    <span className="process-name">{proc.name}</span>
                    <span className={`complexity-badge ${(proc.complexity || 'Medium').toLowerCase()}`}>
                      {proc.complexity || 'Medium'}
                    </span>
                  </div>
                  <p className="process-desc">{proc.description}</p>

                  {/* Cloud Readiness Bar */}
                  {proc.cloud_readiness_score != null && (
                    <div className="cloud-readiness">
                      <div className="cloud-readiness-header">
                        <Cloud size={11} />
                        <span>Cloud Readiness</span>
                        <span
                          className="cloud-readiness-pct"
                          style={{ color: readinessColor(proc.cloud_readiness_score) }}
                        >
                          {proc.cloud_readiness_score}%
                        </span>
                      </div>
                      <div className="readiness-track">
                        <div
                          className="readiness-fill"
                          style={{
                            width: `${proc.cloud_readiness_score}%`,
                            background: readinessColor(proc.cloud_readiness_score),
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* AWS Service Recommendations */}
                  {proc.aws_recommendations?.length > 0 && (
                    <div className="proc-aws-recs">
                      {proc.aws_recommendations.map((svc, j) => (
                        <AWSServiceBadge key={j} service={svc} />
                      ))}
                    </div>
                  )}

                  <div className="process-meta">
                    <span>{proc.business_domain || 'General'}</span>
                    <span>{proc.execution_frequency || 'Batch'}</span>
                    {proc.recommended_approach && (
                      <span className="recommended-approach">
                        <Activity size={10} />
                        {proc.recommended_approach}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ))}
      </div>

      {selectedProcess && (
        <div className="process-details">
          <h3>{selectedProcess.name}</h3>
          <p>{selectedProcess.description}</p>

          {/* Cloud Readiness Detail */}
          {selectedProcess.cloud_readiness_score != null && (
            <div className="detail-readiness">
              <div className="detail-readiness-row">
                <Cloud size={14} />
                <span>Cloud Readiness</span>
                <span
                  className="detail-readiness-pct"
                  style={{ color: readinessColor(selectedProcess.cloud_readiness_score) }}
                >
                  {selectedProcess.cloud_readiness_score}%
                </span>
              </div>
              <div className="readiness-track large">
                <div
                  className="readiness-fill"
                  style={{
                    width: `${selectedProcess.cloud_readiness_score}%`,
                    background: readinessColor(selectedProcess.cloud_readiness_score),
                  }}
                />
              </div>
            </div>
          )}

          {/* Recommended Approach */}
          {selectedProcess.recommended_approach && (
            <div className="detail-approach">
              <Layers size={13} />
              <span className="approach-label">Approach</span>
              <span className="approach-value">{selectedProcess.recommended_approach}</span>
            </div>
          )}

          {/* AWS Services */}
          {selectedProcess.aws_recommendations?.length > 0 && (
            <div className="detail-aws">
              <div className="detail-aws-title">
                <Server size={13} />
                Recommended AWS Services
              </div>
              <div className="detail-aws-badges">
                {selectedProcess.aws_recommendations.map((svc, i) => (
                  <AWSServiceBadge key={i} service={svc} />
                ))}
              </div>
            </div>
          )}

          <div className="detail-grid">
            <div className="detail-item">
              <label>Business Value</label>
              <span className={`value-badge ${(selectedProcess.business_value || 'Medium').toLowerCase()}`}>
                {selectedProcess.business_value || 'Medium'}
              </span>
            </div>
            <div className="detail-item">
              <label>Complexity</label>
              <span>{selectedProcess.complexity || 'Medium'}</span>
            </div>
            <div className="detail-item">
              <label>Domain</label>
              <span>{selectedProcess.business_domain || 'General'}</span>
            </div>
            <div className="detail-item">
              <label>Frequency</label>
              <span>{selectedProcess.execution_frequency || 'Batch'}</span>
            </div>
            <div className="detail-item">
              <label>Confidence</label>
              <span>{selectedProcess.confidence_score || 0}%</span>
            </div>
            <div className="detail-item">
              <label>Source</label>
              <span className="source-file">{selectedProcess.source_file || 'Unknown'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Integration Points Tab — fixed integration_type field + detected evidence
 */
function IntegrationPointsTab({ data }) {
  const integrations = data?.integration_points?.integration_points || []

  if (integrations.length === 0) {
    return (
      <div className="empty-state">
        <p>No integration points found</p>
        <span>Discovery analysis will identify system integrations</span>
      </div>
    )
  }

  // Use integration_type (correct field) with fallback to type
  const grouped = useMemo(() => {
    const groups = {}
    integrations.forEach(int => {
      const type = int.integration_type || int.type || 'Other'
      if (!groups[type]) groups[type] = []
      groups[type].push(int)
    })
    return groups
  }, [integrations])

  const getTypeIcon = (type) => {
    const t = (type || '').toLowerCase()
    if (t.includes('program') || t.includes('call')) return '🔗'
    if (t.includes('database') || t.includes('db') || t.includes('vsam')) return '🗄️'
    if (t.includes('file')) return '📁'
    if (t.includes('api') || t.includes('rest') || t.includes('soap')) return '🔌'
    if (t.includes('queue') || t.includes('message') || t.includes('mq')) return '📨'
    if (t.includes('external') || t.includes('third')) return '🌐'
    return '🔗'
  }

  const getAWSMigrationPath = (type) => {
    const t = (type || '').toLowerCase()
    if (t.includes('program') || t.includes('call')) return 'Refactor as AWS Lambda functions or Step Functions workflows'
    if (t.includes('database') || t.includes('vsam')) return 'Migrate to Amazon RDS / Aurora PostgreSQL'
    if (t.includes('file')) return 'Replace with Amazon S3 object storage'
    if (t.includes('queue') || t.includes('message')) return 'Replace with Amazon SQS / SNS'
    if (t.includes('api')) return 'Expose via Amazon API Gateway + Lambda'
    return 'Evaluate for cloud-native service replacement'
  }

  return (
    <div className="integration-points">
      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} className="integration-group">
          <div className="integration-group-header">
            <div className="integration-group-title">
              <span className="type-icon">{getTypeIcon(type)}</span>
              <h4>{type}</h4>
              <span className="integration-count-badge">{items.length}</span>
            </div>
            <div className="integration-aws-path">
              <Cloud size={12} />
              <span>{getAWSMigrationPath(type)}</span>
            </div>
          </div>
          <div className="integration-list">
            {items.map((item, i) => (
              <div key={i} className="integration-item">
                <div className="integration-header">
                  <span className="integration-name">{item.name || item.target}</span>
                  <span className={`complexity ${(item.complexity || 'medium').toLowerCase()}`}>
                    {item.complexity || 'Medium'}
                  </span>
                </div>
                {item.description && (
                  <p className="integration-desc">{item.description}</p>
                )}

                {/* Detected Evidence — actual CALL statements */}
                {item.detected_evidence?.length > 0 && (
                  <div className="detected-evidence">
                    <div className="evidence-title">
                      <Code2 size={11} />
                      Detected in COBOL Source
                    </div>
                    <div className="evidence-code-block">
                      {item.detected_evidence.map((ev, j) => (
                        <div key={j} className="evidence-line">
                          <span className="evidence-prompt">›</span>
                          <code>{ev}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="integration-meta">
                  {item.programs?.length > 0 && (
                    <span className="program-count">{item.programs.length} programs</span>
                  )}
                  {item.frequency && <span>{item.frequency}</span>}
                  {item.migration_complexity && (
                    <span className="migration-effort">
                      Migration: {item.migration_complexity}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Migration Roadmap Tab — D3 Gantt + phase activity checklists + milestones
 */
function MigrationRoadmapTab({ data }) {
  const svgRef = useRef(null)
  const containerRef = useRef(null)

  const roadmap = data?.migration_roadmap || {}
  const phases = roadmap.phases || []
  const keyBenefits = roadmap.key_benefits || []

  const PHASE_COLORS = ['#7aa2f7', '#9ece6a', '#e0af68', '#bb9af7', '#7dcfff']

  useEffect(() => {
    if (!phases.length || !svgRef.current || !containerRef.current) return

    const container = containerRef.current
    const width = container.clientWidth || 800
    const height = Math.max(300, phases.length * 100)

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)

    svg.selectAll('*').remove()

    const margin = { top: 40, right: 40, bottom: 20, left: 200 }
    const chartWidth = width - margin.left - margin.right
    const chartHeight = height - margin.top - margin.bottom

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Calculate cumulative timeline
    let cumulativeWeeks = 0
    const phaseData = phases.map((phase, i) => {
      const start = cumulativeWeeks
      cumulativeWeeks += getPhaseWeeks(phase) ?? 4
      return {
        ...phase,
        startWeek: start,
        endWeek: cumulativeWeeks,
        durationWeeks: getPhaseWeeks(phase) ?? 4,
        index: i,
      }
    })

    const totalWeeks = roadmap.timeline?.total_weeks || cumulativeWeeks
    const xScale = d3.scaleLinear()
      .domain([0, totalWeeks])
      .range([0, chartWidth])

    const yScale = d3.scaleBand()
      .domain(phaseData.map((_, i) => i))
      .range([0, chartHeight])
      .padding(0.35)

    // Timeline axis
    const xAxis = d3.axisTop(xScale)
      .ticks(Math.min(totalWeeks, 10))
      .tickFormat(d => `Wk ${d}`)

    g.append('g')
      .call(xAxis)
      .selectAll('text')
      .attr('fill', '#565f89')
      .attr('font-size', 10)

    g.select('.domain').attr('stroke', '#414868')
    g.selectAll('.tick line').attr('stroke', '#414868')

    // Grid lines
    g.selectAll('.grid-line')
      .data(xScale.ticks(Math.min(totalWeeks, 10)))
      .join('line')
      .attr('x1', d => xScale(d))
      .attr('x2', d => xScale(d))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#2a2f44')
      .attr('stroke-dasharray', '2,4')

    // Phase bars
    g.selectAll('.phase-bar')
      .data(phaseData)
      .join('rect')
      .attr('class', 'phase-bar')
      .attr('x', d => xScale(d.startWeek))
      .attr('y', d => yScale(d.index))
      .attr('width', d => Math.max(4, xScale(d.endWeek) - xScale(d.startWeek)))
      .attr('height', yScale.bandwidth())
      .attr('fill', (d, i) => PHASE_COLORS[i % PHASE_COLORS.length])
      .attr('rx', 5)
      .attr('opacity', 0.85)

    // Phase labels on left
    g.selectAll('.phase-label')
      .data(phaseData)
      .join('text')
      .attr('class', 'phase-label')
      .attr('x', -10)
      .attr('y', d => yScale(d.index) + yScale.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#c0caf5')
      .attr('font-size', 12)
      .attr('font-weight', 500)
      .text(d => d.name)

    // Duration labels on bars
    g.selectAll('.duration-label')
      .data(phaseData)
      .join('text')
      .attr('class', 'duration-label')
      .attr('x', d => xScale(d.startWeek) + (xScale(d.endWeek) - xScale(d.startWeek)) / 2)
      .attr('y', d => yScale(d.index) + yScale.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#1a1b26')
      .attr('font-size', 11)
      .attr('font-weight', 700)
      .text(d => d.durationWeeks != null ? `${d.durationWeeks}w` : '')

  }, [phases])

  if (phases.length === 0) {
    return (
      <div className="empty-state">
        <p>No migration roadmap available</p>
        <span>Run discovery to generate a phased migration plan</span>
      </div>
    )
  }

  return (
    <div className="migration-roadmap">

      {/* Key Benefits */}
      {keyBenefits.length > 0 && (
        <div className="key-benefits-section">
          <h4><Zap size={16} /> Key Benefits</h4>
          <div className="key-benefits-grid">
            {keyBenefits.map((benefit, i) => (
              <div key={i} className="benefit-card">
                <CheckCircle size={15} className="benefit-icon" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline header */}
      <div className="roadmap-header">
        <div className="timeline-info">
          <Calendar size={16} />
          <span>
            Total: {roadmap.timeline?.total_months || 0} months
            {roadmap.timeline?.total_weeks ? ` (${roadmap.timeline.total_weeks} weeks)` : ''}
          </span>
        </div>
      </div>

      {/* Gantt chart */}
      <div className="roadmap-chart" ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Phase cards with activities + milestones */}
      <div className="phase-details">
        {phases.map((phase, i) => {
          const color = PHASE_COLORS[i % PHASE_COLORS.length]
          return (
            <div
              key={i}
              className="phase-card"
              style={{ borderTopColor: color }}
            >
              <div className="phase-header">
                <span
                  className="phase-number"
                  style={{ background: color + '25', color, border: `1px solid ${color}50` }}
                >
                  Phase {phase.phase || i + 1}
                </span>
                <span className="phase-name">{phase.name}</span>
                {getPhaseWeeks(phase) != null && (
                  <span className="phase-duration-pill" style={{ color }}>
                    {getPhaseWeeks(phase)}w
                  </span>
                )}
              </div>

              <p className="phase-description">{phase.description}</p>

              {/* Activities checklist */}
              {phase.activities?.length > 0 && (
                <div className="phase-activities">
                  <div className="phase-section-label">
                    <CheckCircle size={11} style={{ color }} />
                    Activities
                  </div>
                  <ul className="activity-list">
                    {phase.activities.map((act, j) => (
                      <li key={j}>
                        <span className="activity-dot" style={{ background: color }} />
                        {act}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Milestones */}
              {phase.milestones?.length > 0 && (
                <div className="phase-milestones">
                  <div className="phase-section-label">
                    <Target size={11} style={{ color: '#e0af68' }} />
                    Milestones
                  </div>
                  <ul className="milestone-list">
                    {phase.milestones.map((m, j) => (
                      <li key={j}>
                        <span className="milestone-diamond" />
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {phase.components?.length > 0 && (
                <div className="phase-components">
                  {phase.components.map((comp, j) => (
                    <span key={j} className="component-badge">{comp}</span>
                  ))}
                </div>
              )}

              {phase.dependencies?.length > 0 && (
                <div className="phase-meta">
                  <span className="dependencies">
                    Depends on: {phase.dependencies.join(', ')}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Main DiscoveryView Component
 */
function DiscoveryView({ onBack, analysisContext }) {
  const [activeTab, setActiveTab] = useState('summary')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const jobId = analysisContext?.jobs?.discovery_job_id || analysisContext?.jobId

  useEffect(() => {
    const fetchData = async () => {
      if (!jobId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const result = await getDiscoveryResults(jobId)
        setData(result)
      } catch (err) {
        console.error('Failed to fetch discovery results:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [jobId])

  const handleRefresh = useCallback(async () => {
    setLoading(true)
    try {
      if (jobId) {
        const result = await getDiscoveryResults(jobId)
        setData(result)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [jobId])

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <Loader2 size={32} className="spin" />
          <p>Loading discovery results...</p>
        </div>
      )
    }

    if (error) {
      return (
        <div className="error-state">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      )
    }

    switch (activeTab) {
      case 'summary':
        return <ExecutiveSummaryTab data={data} />
      case 'processes':
        return <BusinessProcessesTab data={data} />
      case 'integrations':
        return <IntegrationPointsTab data={data} />
      case 'roadmap':
        return <MigrationRoadmapTab data={data} />
      default:
        return null
    }
  }

  return (
    <div className="discovery-view">
      {/* Header */}
      <div className="view-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="header-title">
          <Compass size={20} />
          <h2>Discovery Analysis</h2>
        </div>
        <div></div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-nav">
        {Object.entries(TABS).map(([key, tab]) => {
          const Icon = tab.icon
          return (
            <button
              key={key}
              className={`tab-btn ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  )
}

export default DiscoveryView
